import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Landmark, ListTree, Search, ShieldCheck } from "lucide-react";
import { EmptyState, ErrorState, HeaderRow, LoadingState, rowLink, TableShell, TH } from "@/components/PageUi";
import { InfoHint } from "@/components/InfoHint";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCalendarDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import { BooksPeriodScreen, money, num, useChart } from "./shared";
import { addDays, periodLabel, type Period } from "./period";
import { ExportButton, csvName, downloadCsv, drillKey, useDrilldown, type DrillTarget } from "./ReportKit";
import type { Book } from "./types";

const TYPES = [
  { key: "all", label: "All" },
  { key: "asset", label: "Assets" },
  { key: "liability", label: "Liabilities" },
  { key: "equity", label: "Equity" },
  { key: "revenue", label: "Revenue" },
  { key: "expense", label: "Expenses" },
] as const;
type TypeKey = (typeof TYPES)[number]["key"];

/** The chart's classifications folded to the five types, as the reports group them; anything else is "other". */
function typeOf(classification?: string | null): Exclude<TypeKey, "all"> | "other" {
  switch ((classification ?? "").toLowerCase()) {
    case "asset":
      return "asset";
    case "liability":
      return "liability";
    case "equity":
      return "equity";
    case "revenue":
    case "income":
    case "other_income":
      return "revenue";
    case "expense":
    case "costs_of_sales":
    case "other_expense":
      return "expense";
    default:
      // Apideck also knows `other`; such an account is still listed (under All), so the chart matches the trial balance.
      return "other";
  }
}

const humanise = (t?: string | null) => (t ? (t.charAt(0).toUpperCase() + t.slice(1)).replace(/_/g, " ") : "");

/**
 * The chart of accounts, after Mercury's: every account with its balance at the period's end and what moved through
 * it in the period, by type. Accounts Dolfin posts to itself, and bank accounts, say so.
 */
export function ChartOfAccounts() {
  return (
    <BooksPeriodScreen basis title="Chart of accounts" subtitle="Every account, its balance at the end of the period and what moved through it.">
      {(book, period) => <ChartBody book={book} period={period} />}
    </BooksPeriodScreen>
  );
}

function ChartBody({ book, period }: { book: Book; period: Period }) {
  const { books } = useBooks();
  const chart = useChart();
  const drill = useDrilldown(period);
  const [params, setParams] = useSearchParams();
  const type = (TYPES.find((t) => t.key === params.get("type"))?.key ?? "all") as TypeKey;
  const [query, setQuery] = useState("");
  const dayBefore = addDays(period.fromDate, -1);
  const end = useLoad(
    () => books.GET("/v1/books/current/trial-balance", { params: { query: { Date: period.asAt, Basis: period.basis } } }),
    [books, period.asAt, period.basis],
  );
  const start = useLoad(
    () => books.GET("/v1/books/current/trial-balance", { params: { query: { Date: dayBefore, Basis: period.basis } } }),
    [books, dayBefore, period.basis],
  );
  // Income and expense balances restart each financial year, so their activity comes from the P&L, not a difference.
  const pl = useLoad(
    () => books.GET("/v1/books/current/profit-and-loss", { params: { query: { From: period.fromDate, To: period.toDate, Basis: period.basis } } }),
    [books, period.fromDate, period.toDate, period.basis],
  );

  if ((chart.loading && !chart.data) || (end.loading && !end.data) || (start.loading && !start.data) || (pl.loading && !pl.data))
    return <LoadingState label="Loading the chart of accounts" />;
  const error = chart.error ?? end.error ?? start.error ?? pl.error;
  if (error) return <ErrorState message={apiErrorMessage(error, "Couldn't load the chart of accounts.")} onRetry={() => void Promise.all([chart.reload(), end.reload(), start.reload(), pl.reload()])} />;

  const control = controlAccounts(book);
  const banks = new Set((book.bankAccounts ?? []).map((a) => a.ledgerAccountId));
  const balanceAt = (rows: typeof end.data, id: string) => {
    const r = rows?.rows?.find((x) => x.ledgerAccountId === id);
    return r ? num(r.debit) - num(r.credit) : 0;
  };
  const plAmount = new Map((pl.data?.groups ?? []).flatMap((g) => (g.rows ?? []).map((r) => [r.ledgerAccountId, num(r.amount)] as const)));

  const accounts = (chart.data ?? [])
    .map((a) => {
      const t = typeOf(a.classification);
      const sign = t === "liability" || t === "equity" || t === "revenue" ? -1 : 1;
      const balance = sign * balanceAt(end.data, a.id!);
      const activity = t === "revenue" || t === "expense" ? (plAmount.get(a.id!) ?? 0) : balance - sign * balanceAt(start.data, a.id!);
      return { a, t, sign, balance, activity };
    })
    // An archived account stays listed while it still holds something.
    .filter((x) => x.a.isActive !== false || x.balance !== 0 || x.activity !== 0)
    .sort((x, y) => (x.a.displayId ?? "~").localeCompare(y.a.displayId ?? "~") || x.a.name.localeCompare(y.a.name));

  const counts = new Map(TYPES.map((t) => [t.key, t.key === "all" ? accounts.length : accounts.filter((x) => x.t === t.key).length]));
  const q = query.trim().toLowerCase();
  const shown = accounts.filter(
    (x) => (type === "all" || x.t === type) && (!q || x.a.name.toLowerCase().includes(q) || (x.a.displayId ?? "").toLowerCase().includes(q)),
  );
  const caption = periodLabel(period.from, period.to);
  const label = (x: (typeof accounts)[number]) => (x.a.displayId ? `${x.a.displayId} · ${x.a.name}` : x.a.name);
  const bankAccountOf = new Map((book.bankAccounts ?? []).map((b) => [b.ledgerAccountId, b.bankAccountId ?? undefined]));
  const targets: DrillTarget[] = shown.map((x) => ({
    accountId: x.a.id!,
    bankAccountId: bankAccountOf.get(x.a.id),
    label: label(x),
    from: period.fromDate,
    to: period.toDate,
    amount: x.activity,
    natural: x.sign === 1 ? "debit" : "credit",
    caption: `Activity in ${caption}`,
  }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" aria-label="Account type" className="inline-flex flex-wrap items-center gap-1 rounded-md bg-muted p-0.5 text-muted-foreground">
          {TYPES.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={type === t.key}
              onClick={() =>
                setParams(
                  (p) => {
                    if (t.key === "all") p.delete("type");
                    else p.set("type", t.key);
                    return p;
                  },
                  { replace: true },
                )
              }
              className={cn(
                "rounded-sm px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                type === t.key ? "bg-background text-foreground shadow-sm" : "hover:text-foreground",
              )}
            >
              {t.label} <span className="tabular-nums text-muted-foreground">{counts.get(t.key)}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find an account" aria-label="Find an account" className="h-8 w-44 pl-8 text-sm" />
          </div>
          <ExportButton
            onExport={() =>
              downloadCsv(csvName("chart-of-accounts", period, period.asAt), [
                ["Code", "Account", "Type", "Classification", `Activity ${caption}`, `Balance ${period.asAt}`],
                ...shown.map((x) => [x.a.displayId ?? "", x.a.name, humanise(x.a.accountType), x.t, x.activity, x.balance]),
              ])
            }
          />
        </div>
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={ListTree} title={q ? "No account matches" : "No accounts of this type"} />
      ) : (
        <TableShell>
          <Table>
            <TableHeader>
              <HeaderRow>
                <TableHead className={cn(TH, "hidden w-20 @xl:table-cell")}>Code</TableHead>
                <TableHead className={TH}>Account</TableHead>
                <TableHead className={cn(TH, "hidden @2xl:table-cell")}>Type</TableHead>
                <TableHead className={cn(TH, "text-right")}>Activity</TableHead>
                <TableHead className={cn(TH, "text-right")}>
                  <span className="inline-flex items-center gap-1">
                    Balance
                    <InfoHint label="About balances">
                      At {formatCalendarDate(period.asAt)}. Income and expense accounts show the financial year to date, as the trial balance does.
                    </InfoHint>
                  </span>
                </TableHead>
              </HeaderRow>
            </TableHeader>
            <TableBody>
              {shown.map((x, i) => {
                const role = control.get(x.a.id!);
                return (
                  <TableRow
                    key={x.a.id}
                    {...rowLink(() => drill.open(targets, i))}
                    data-state={drill.openKey === drillKey(targets[i]!) ? "selected" : undefined}
                    className={cn(rowLink(() => undefined).className, x.a.isActive === false && "text-muted-foreground")}
                  >
                    <TableCell className="hidden tabular-nums text-muted-foreground @xl:table-cell">{x.a.displayId ?? "-"}</TableCell>
                    {/* w-full + max-w-0: the name takes the spare width and truncates, so the figures stay in view. */}
                    <TableCell className="w-full max-w-0">
                      <span className="flex min-w-0 items-center gap-1.5">
                        {/* In a narrow pane the code joins the name rather than taking a column. */}
                        {x.a.displayId ? <span className="shrink-0 tabular-nums text-muted-foreground @xl:hidden">{x.a.displayId}</span> : null}
                        <span className="truncate font-medium">{x.a.name}</span>
                        {banks.has(x.a.id) ? (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            <Landmark className="size-3" aria-label="Bank account" /> <span className="hidden @3xl:inline">Bank</span>
                          </span>
                        ) : null}
                        {role ? (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            <ShieldCheck className="size-3" aria-label={`Dolfin posts here: ${role}`} /> <span className="hidden @3xl:inline">{role}</span>
                          </span>
                        ) : null}
                        {x.a.isActive === false ? <span className="shrink-0 text-[10px] uppercase tracking-wide">Archived</span> : null}
                      </span>
                    </TableCell>
                    <TableCell className="hidden text-muted-foreground @2xl:table-cell">{humanise(x.a.accountType ?? x.a.classification)}</TableCell>
                    <TableCell className={cn("text-right tabular-nums", x.activity === 0 && "text-muted-foreground/60")}>{money(x.activity)}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{money(x.balance)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableShell>
      )}
      {drill.sheet}
    </div>
  );
}

/** The accounts Dolfin posts to on its own, by what it uses them for. */
function controlAccounts(book: Book) {
  const c = book.controlAccounts ?? {};
  const roles: Array<[string | null | undefined, string]> = [
    [c.accountsReceivableLedgerAccountId, "Receivables"],
    [c.accountsPayableLedgerAccountId, "Payables"],
    [c.vatLedgerAccountId, "VAT"],
    [c.uncategorisedLedgerAccountId, "Uncategorised"],
    [c.stripeClearingLedgerAccountId, "Stripe clearing"],
    [c.stripeFeesLedgerAccountId, "Stripe fees"],
    [c.openingBalanceDifferenceLedgerAccountId, "Opening difference"],
    [c.retainedEarningsLedgerAccountId, "Retained earnings"],
  ];
  return new Map(roles.filter(([id]) => id).map(([id, role]) => [id!, role]));
}
